import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Skeleton,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QueryOperation } from "../../code/search-query/search-query";
import { nameOf } from "../../utils/typescript";
import { addCompany, getCompanyIndustries } from "../../repository/data.repository";
import { Company, CompanyIndustry } from "../../repository/data.default";
import { retry, getProblemMessage } from "../../utils/http";
import ComboBox from "../../controls/ComboBox";
import { useSelector } from "../../store/store";
import { authorizeSelectors } from "../../store/authorize";

export interface CompanySubCompaniesPanelProps {
  readonly open: boolean;
  readonly parentCompanyUid: string | undefined;
  readonly parentCompanyName?: string;
  readonly reloadToken?: string | number;
  readonly onChange?: () => void;
  readonly onCountLoaded?: (count: number) => void;
  /** Root + every descendant discovered by CompanyView's useCompanyScope. The list below is drawn from
   *  this directly — no extra fetch — and the Add dialog's "Parent" picker lets the operator place a
   *  new sub-company anywhere inside the branch. */
  readonly scopeCompanies: Company[];
}

/**
 * All sub-companies under the parent (recursively, not just direct children). Add dialog lets the
 * operator pick which company inside the branch owns the new sub-company — defaults to the root.
 */
function CompanySubCompaniesPanel(props: CompanySubCompaniesPanelProps) {
  const permissions = useSelector(authorizeSelectors.permissions);
  const canCreate = permissions.can("Companies", "create");
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  // Descendants = scope minus the root itself.
  const descendants = useMemo(
    () => props.scopeCompanies.filter((c) => c.uid !== props.parentCompanyUid),
    [props.scopeCompanies, props.parentCompanyUid],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return descendants;
    return descendants.filter((c) => c.name.toLowerCase().startsWith(q));
  }, [descendants, search]);

  const everLoadedRef = useRef(false);

  useEffect(() => {
    everLoadedRef.current = true;
    props.onCountLoaded?.(descendants.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descendants.length, props.reloadToken]);

  if (!props.open) return null;

  const showSkeleton = !everLoadedRef.current && props.scopeCompanies.length === 0;

  return (
    <Card
      size="small"
      title={
        <Space>
          <Typography.Text strong>Sub Companies (whole branch)</Typography.Text>
          <Typography.Text type="secondary">({descendants.length})</Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Input.Search
            placeholder="Search by name…"
            allowClear
            size="small"
            style={{ width: 200 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            type="primary"
            size="small"
            disabled={!canCreate || !props.parentCompanyUid}
            onClick={() => setAddOpen(true)}
          >
            + Add Sub Company
          </Button>
        </Space>
      }
    >
      {showSkeleton ? (
        <Skeleton active paragraph={{ rows: 3 }} title={false} />
      ) : (
        <Table
          size="small"
          rowKey={(r) => r.uid}
          dataSource={filtered}
          pagination={filtered.length > 10 ? { pageSize: 10 } : false}
          locale={{ emptyText: "No sub companies in this branch yet." }}
          columns={[
            { title: "Name", dataIndex: "name", key: "name" },
            {
              title: "Parent",
              dataIndex: "parentCompanyName",
              key: "parentCompanyName",
            },
            { title: "Industry", dataIndex: "domainName", key: "domainName" },
            { title: "Address", dataIndex: "address", key: "address", ellipsis: true },
            { title: "Email", dataIndex: "email", key: "email" },
            { title: "Phone", dataIndex: "phoneNumber", key: "phoneNumber" },
            {
              title: "",
              key: "actions",
              width: 110,
              render: (_, record) => (
                <Button
                  size="small"
                  type="link"
                  onClick={() =>
                    navigate(`/companies/tree?companyUid=${record.uid}`)
                  }
                >
                  View in Tree
                </Button>
              ),
            },
          ]}
        />
      )}
      <AddSubCompanyDialog
        open={addOpen}
        scopeCompanies={props.scopeCompanies}
        defaultParentUid={props.parentCompanyUid}
        onCancel={() => setAddOpen(false)}
        onFinish={() => {
          setAddOpen(false);
          props.onChange?.();
        }}
      />
    </Card>
  );
}

function AddSubCompanyDialog(props: {
  open: boolean;
  scopeCompanies: Company[];
  defaultParentUid: string | undefined;
  onCancel: () => void;
  onFinish: () => void;
}) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!props.open) {
      form.resetFields();
      setError("");
    } else {
      const def = props.scopeCompanies.find((c) => c.uid === props.defaultParentUid);
      if (def) form.setFieldsValue({ parentCompany: { display: def.name, value: def.uid } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const industriesRequest = (search: string) =>
    retry(() =>
      getCompanyIndustries({
        searchQuery: {
          conditions: [
            {
              field: nameOf<CompanyIndustry>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((d) => ({ display: d.name, value: d.uid, dataItem: d })),
    );

  // Local picker — no API call. Scope is already loaded by CompanyView.
  const parentCompanyRequest = (search: string) => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? props.scopeCompanies.filter((c) => c.name.toLowerCase().startsWith(q))
      : props.scopeCompanies;
    return Promise.resolve(
      filtered.map((c) => ({ display: c.name, value: c.uid, dataItem: c })),
    );
  };

  async function onSubmit(): Promise<void> {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setError("");
      await addCompany({
        name: values.name,
        address: values.address,
        email: values.email,
        phoneNumber: values.phoneNumber,
        domainUid: values.domain?.value,
        parentCompanyUid: values.parentCompany?.value,
      });
      message.success(`Sub company "${values.name}" created under "${values.parentCompany?.display}".`);
      setSaving(false);
      props.onFinish();
    } catch (err: any) {
      setSaving(false);
      if (err?.errorFields) return;
      setError(getProblemMessage(err));
    }
  }

  return (
    <Modal
      open={props.open}
      title="Add Sub Company"
      onCancel={props.onCancel}
      onOk={onSubmit}
      okText="Create"
      confirmLoading={saving}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="parentCompany"
          label="Parent company (place the new sub-company under)"
          rules={[{ required: true, message: "Parent company is required" }]}
        >
          <ComboBox request={parentCompanyRequest} />
        </Form.Item>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="address" label="Address">
          <Input />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[{ type: "email", message: "Invalid email format" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="phoneNumber" label="Phone Number">
          <Input />
        </Form.Item>
        <Form.Item
          name="domain"
          label="Industry"
          rules={[{ required: true, message: "Industry is required" }]}
        >
          <ComboBox request={industriesRequest} />
        </Form.Item>
      </Form>
      {error && (
        <Typography.Text type="danger" className="mt-2 block">
          {error}
        </Typography.Text>
      )}
    </Modal>
  );
}

export default CompanySubCompaniesPanel;
